import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AccessRule,
  type Impact,
  ImpactSummary,
  type MemberRole,
  type Principal,
  type PrincipalType,
  PrincipalSelect,
  ASSIGNABLE_ROLES,
  useAccessRules,
  useBoardLabelCatalog,
  useCreateAccessRule,
  useCustomRoles,
  useDeleteAccessRule,
  useTeams,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox } from '@/shared/ui';

import styles from './Admin.module.css';

export function RulesPanel({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const rules = useAccessRules(workspace.id);
  const teams = useTeams(workspace.id);
  const labels = useBoardLabelCatalog(workspace.id);
  const customRoles = useCustomRoles(workspace.id);
  const create = useCreateAccessRule(workspace.id);
  const remove = useDeleteAccessRule(workspace.id);
  const [principalType, setPrincipalType] = useState<PrincipalType>('team');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [granted, setGranted] = useState<MemberRole>('editor');
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [match, setMatch] = useState<'any' | 'all'>('any');
  const [impact, setImpact] = useState<Impact | null>(null);

  const subjectName = (rule: AccessRule): string =>
    rule.subject_kind === 'team'
      ? ((teams.data ?? []).find((x) => x.id === rule.team_id)?.name ?? '…')
      : rule.subject_kind === 'role'
        ? t(`roles.${rule.subject_role ?? 'member'}`)
        : ((customRoles.data ?? []).find((x) => x.id === rule.custom_role_id)?.name ?? '…');
  const payload = (dryRun: boolean) => ({
    subject_kind: principal?.kind ?? 'team',
    ...(principal?.kind === 'team' && { team_id: principal.id }),
    ...(principal?.kind === 'role' && { subject_role: principal.role }),
    ...(principal?.kind === 'custom_role' && { custom_role_id: principal.id }),
    granted_role: granted,
    label_ids: labelIds,
    label_match: match,
    dry_run: dryRun,
  });
  const ready = principal !== null && labelIds.length > 0;

  return (
    <section className={styles.card} aria-labelledby="rules-title">
      <h3 id="rules-title">{t('workspaceAdmin.rulesTitle')}</h3>
      <p className={styles.note}>{t('workspaceAdmin.rulesHint')}</p>
      <ul className={styles.list}>
        {(rules.data ?? []).map((rule) => (
          <li key={rule.id} className={styles.row} data-testid="access-rule">
            <span className={styles.grow}>
              {t('workspaceAdmin.ruleText', {
                subject: subjectName(rule),
                role: t(`roles.${rule.granted_role}`),
              })}
              <span className={styles.meta}>
                {rule.board_id
                  ? (rule.board_name ?? t('workspaceAdmin.hiddenBoard'))
                  : rule.label_ids
                      .map((id) => (labels.data ?? []).find((l) => l.id === id)?.name ?? '…')
                      .join(rule.label_match === 'all' ? ' + ' : ' / ')}
              </span>
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => {
                remove.mutate({ ruleId: rule.id });
              }}
            >
              {t('workspaceAdmin.deleteRule')}
            </Button>
          </li>
        ))}
      </ul>
      <div className={styles.ruleForm}>
        <div className={styles.field2}>
          <span className={styles.fieldLabel}>{t('access.principal.who')}</span>
          <PrincipalSelect
            type={principalType}
            onTypeChange={setPrincipalType}
            value={principal}
            onChange={setPrincipal}
            teams={teams.data ?? []}
            customRoles={customRoles.data ?? []}
          />
        </div>
        <div className={styles.field2}>
          <span className={styles.fieldLabel}>{t('access.principal.grants')}</span>
          <Combobox
            label={t('access.principal.grants')}
            value={granted}
            size="sm"
            options={ASSIGNABLE_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
            onChange={(next) => {
              setGranted(next as MemberRole);
            }}
          />
        </div>
        <div className={styles.field2}>
          <span className={styles.fieldLabel}>{t('workspaceAdmin.ruleMatch')}</span>
          <Combobox
            label={t('workspaceAdmin.ruleMatch')}
            value={match}
            size="sm"
            options={[
              { value: 'any', label: t('filter.matchAny') },
              { value: 'all', label: t('filter.matchAll') },
            ]}
            onChange={(next) => {
              setMatch(next === 'all' ? 'all' : 'any');
            }}
          />
        </div>
        <fieldset className={styles.labelPick}>
          <legend>{t('workspaceAdmin.ruleLabels')}</legend>
          {(labels.data ?? []).map((l) => {
            const on = labelIds.includes(l.id);
            return (
              <button
                key={l.id}
                type="button"
                aria-pressed={on}
                className={`${styles.labelChip} ${on ? styles.labelChipOn : ''}`}
                onClick={() => {
                  setLabelIds(on ? labelIds.filter((x) => x !== l.id) : [...labelIds, l.id]);
                  setImpact(null);
                }}
              >
                <span className={styles.swatch} style={{ background: l.color }} />
                {l.name}
              </button>
            );
          })}
          {(labels.data ?? []).length === 0 && (
            <span className={styles.note}>{t('workspaceAdmin.noLabelsYet')}</span>
          )}
        </fieldset>
      </div>
      {impact && <ImpactSummary impact={impact} />}
      <FormError error={create.error ?? remove.error} />
      <div className={styles.actions}>
        <Button
          disabled={!ready || create.isPending}
          onClick={() => {
            create.mutate(payload(true), {
              onSuccess: (r) => {
                setImpact(r.impact);
              },
            });
          }}
        >
          {t('access.previewImpact')}
        </Button>
        <Button
          variant="primary"
          disabled={!ready || create.isPending}
          onClick={() => {
            create.mutate(payload(false), {
              onSuccess: () => {
                setImpact(null);
                setLabelIds([]);
              },
            });
          }}
        >
          {t('workspaceAdmin.addRule')}
        </Button>
      </div>
    </section>
  );
}
